import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { BottomNavigation, BottomNavigationAction, Box, CssBaseline, Stack, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { updateLeaderboardPlayerName } from './api/leaderboard_api';
import { prepareInitialMediaPlan } from './game/mediaData';
import { createMediaPreload, getMediaPreloadKey } from './game/mediaPreload';
import GameScreen from './screens/GameScreen';
import HighScoresScreen from './screens/HighScoresScreen';
import NotFoundScreen from './screens/NotFoundScreen';
import ProfileScreen from './screens/ProfileScreen';
import ResultsScreen from './screens/ResultsScreen';
import SettingsScreen from './screens/SettingsScreen';
import StartScreen from './screens/StartScreen';
import {
  getOrCreateDeviceId,
  getOrCreatePlayerName,
  resetDeviceId,
  savePlayerName,
} from './storage/deviceIdentity';
import { getSettings } from './storage/settingsStorage';

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#101413',
      paper: '#171d1b',
    },
    primary: {
      main: '#00c2a8',
    },
    secondary: {
      main: '#ffcc4d',
    },
    error: {
      main: '#ff5d73',
    },
    text: {
      primary: '#f5f7f6',
      secondary: '#aeb9b5',
    },
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontSize: '2.45rem',
      fontWeight: 900,
      lineHeight: 1,
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: 850,
      lineHeight: 1.08,
    },
    button: {
      fontWeight: 800,
      textTransform: 'none',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

const routes = [
  {
    path: '/',
    label: 'Start',
    Screen: StartScreen,
    showInNav: false,
  },
  {
    path: '/game',
    label: 'Game',
    Screen: GameScreen,
    icon: <CasinoRoundedIcon />,
    showInNav: true,
  },
  {
    path: '/results',
    label: 'Results',
    Screen: ResultsScreen,
    showInNav: false,
  },
  {
    path: '/scores',
    label: 'Leaderboard',
    Screen: HighScoresScreen,
    icon: <EmojiEventsRoundedIcon />,
    showInNav: true,
  },
  {
    path: '/settings',
    label: 'Settings',
    Screen: SettingsScreen,
    icon: <SettingsRoundedIcon />,
    showInNav: true,
  },
  {
    path: '/profile',
    label: 'Profile',
    Screen: ProfileScreen,
    icon: <PersonRoundedIcon />,
    showInNav: true,
  },
];

const navRoutes = routes.filter((route) => route.showInNav);
const FEEDBACK_AUTO_CLOSE_MS = 1000;
const SESSION_SEEN_MEDIA_KEY_PREFIX = 'ai-or-not:session-seen-media';
const SESSION_GAME_STATE_KEY_PREFIX = 'ai-or-not:game-state';

function readSessionSeenMediaIds(deviceId) {
  try {
    const storedIds = window.sessionStorage.getItem(`${SESSION_SEEN_MEDIA_KEY_PREFIX}:${deviceId}`);
    const parsedIds = storedIds ? JSON.parse(storedIds) : [];

    return Array.isArray(parsedIds) ? new Set(parsedIds) : new Set();
  } catch {
    return new Set();
  }
}

function readSessionGameState(deviceId) {
  try {
    const storedState = window.sessionStorage.getItem(`${SESSION_GAME_STATE_KEY_PREFIX}:${deviceId}`);
    const parsedState = storedState ? JSON.parse(storedState) : null;

    if (!parsedState || !Array.isArray(parsedState.mediaItems)) {
      return null;
    }

    return {
      mediaItems: parsedState.mediaItems,
      roundIndex: Number(parsedState.roundIndex) || 0,
    };
  } catch {
    return null;
  }
}

function getCurrentRoute() {
  const pathname = window.location.pathname;

  return routes.find((route) => route.path === pathname) || {
    path: pathname,
    label: 'Not Found',
    Screen: NotFoundScreen,
  };
}

function App() {
  const [, setLocationVersion] = useState(0);
  const [deviceId, setDeviceId] = useState(() => getOrCreateDeviceId());
  const [playerName, setPlayerName] = useState(() => getOrCreatePlayerName());
  const [appFeedback, setAppFeedback] = useState(null);
  const firstMediaPreloadRef = useRef({ key: '', preload: null });
  const currentRoute = getCurrentRoute();
  const Screen = currentRoute.Screen;

  useEffect(() => {
    const handlePopState = () => setLocationVersion((version) => version + 1);

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleNavigate = (event, nextPath) => {
    if (nextPath !== window.location.pathname) {
      window.history.pushState({}, '', nextPath);
      setLocationVersion((version) => version + 1);
    }
  };

  const navigate = (nextPath) => {
    handleNavigate(null, nextPath);
  };

  const handleResetDeviceId = () => {
    setDeviceId(resetDeviceId());
  };

  const handleSavePlayerName = async (nextPlayerName) => {
    const savedPlayerName = savePlayerName(nextPlayerName);

    setPlayerName(savedPlayerName);
    await updateLeaderboardPlayerName({
      deviceId,
      playerName: savedPlayerName,
    });

    return savedPlayerName;
  };

  const handleFeedbackClose = () => {
    if (appFeedback?.onClose) {
      appFeedback.onClose();
    }

    setAppFeedback(null);
  };

  const preloadFirstGameMedia = useCallback(() => {
    const savedGameState = readSessionGameState(deviceId);

    if (savedGameState?.mediaItems[savedGameState.roundIndex]) {
      const preloadTarget = savedGameState.mediaItems[savedGameState.roundIndex];
      const preloadKey = getMediaPreloadKey(preloadTarget);

      if (!preloadKey || firstMediaPreloadRef.current.key === preloadKey) {
        return Promise.resolve();
      }

      const preload = createMediaPreload(preloadTarget, getSettings());

      if (preload) {
        if (firstMediaPreloadRef.current.preload) {
          firstMediaPreloadRef.current.preload.dispose();
        }

        firstMediaPreloadRef.current = {
          key: preloadKey,
          preload,
        };
      }

      return Promise.resolve();
    }

    return prepareInitialMediaPlan(deviceId, readSessionSeenMediaIds(deviceId), 0).then((mediaPlan) => {
      const preloadTarget = mediaPlan.mediaItems[0];
      const preloadKey = getMediaPreloadKey(preloadTarget);

      if (!preloadKey || firstMediaPreloadRef.current.key === preloadKey) {
        return;
      }

      const preload = createMediaPreload(preloadTarget, getSettings());

      if (!preload) {
        return;
      }

      if (firstMediaPreloadRef.current.preload) {
        firstMediaPreloadRef.current.preload.dispose();
      }

      firstMediaPreloadRef.current = {
        key: preloadKey,
        preload,
      };
    });
  }, [deviceId]);

  useEffect(() => {
    if (!appFeedback) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      if (appFeedback.onClose) {
        appFeedback.onClose();
      }

      setAppFeedback(null);
    }, FEEDBACK_AUTO_CLOSE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [appFeedback]);

  useEffect(() => {
    let isCancelled = false;

    if (currentRoute.path !== '/' && currentRoute.path !== '/game') {
      if (firstMediaPreloadRef.current.preload) {
        firstMediaPreloadRef.current.preload.dispose();
        firstMediaPreloadRef.current = { key: '', preload: null };
      }

      return undefined;
    }

    if (currentRoute.path === '/game') {
      return undefined;
    }

    preloadFirstGameMedia()
      .then(() => {
        if (isCancelled) {
          return;
        }
      })
      .catch(() => {});

    return () => {
      isCancelled = true;
    };
  }, [currentRoute.path, preloadFirstGameMedia]);

  useEffect(() => {
    return () => {
      if (firstMediaPreloadRef.current.preload) {
        firstMediaPreloadRef.current.preload.dispose();
      }
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="app-shell">
        <Box component="main" className="app-content">
          <Screen
            routes={routes}
            currentRoute={currentRoute}
            deviceId={deviceId}
            playerName={playerName}
            onNavigate={navigate}
            onResetDeviceId={handleResetDeviceId}
            onSavePlayerName={handleSavePlayerName}
            onShowFeedback={setAppFeedback}
            onPreloadFirstGameMedia={preloadFirstGameMedia}
          />

          {appFeedback && (
            <Box
              className={`answer-burst page-answer-burst ${appFeedback.isCorrect ? 'answer-burst-correct' : 'answer-burst-wrong'}`}
              role="button"
              tabIndex={0}
              onClick={handleFeedbackClose}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleFeedbackClose();
                }
              }}
            >
              <Box className="particle-field" aria-hidden="true">
                {Array.from({ length: 18 }).map((_, index) => (
                  <span key={index} className="particle" style={{ '--i': index }} />
                ))}
              </Box>
              <Box className="burst-ring" />
              <Stack spacing={0.75} className="burst-copy">
                <Typography className="burst-title">{appFeedback.title}</Typography>
                <Typography className="burst-subtitle">
                  {appFeedback.isCorrect ? 'You nailed it' : `It was ${appFeedback.answerLabel}`}
                </Typography>
                <Typography className="burst-close">Tap to continue</Typography>
              </Stack>
            </Box>
          )}

          <Box className="developer-credit-wrap">
            <Typography
              component="a"
              className="developer-credit"
              href="https://www.quantummindsoftware.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Developed by QuantumMind Software
            </Typography>
          </Box>
        </Box>
        <BottomNavigation
          className="bottom-nav"
          value={navRoutes.some((route) => route.path === currentRoute.path) ? currentRoute.path : '/game'}
          onChange={handleNavigate}
          showLabels
        >
          {navRoutes.map((route) => (
            <BottomNavigationAction
              key={route.path}
              label={route.label}
              value={route.path}
              icon={route.icon}
            />
          ))}
        </BottomNavigation>
      </Box>
    </ThemeProvider>
  );
}

export default App;
